import { useEffect, useRef, useState } from 'react';
import { useOverlay } from '@/hooks/useOverlay';
import type { OrgChartDoc, Selection } from '../types';

interface DrillDownPanelProps {
  doc: OrgChartDoc;
  selection: Selection;
  onClose: () => void;
  onSelect: (s: Selection) => void;
}

function mail(person: { name?: string }): string {
  return (person.name ?? '').toLowerCase().replace(/[^a-z ]/g, '').replace(/ +/g, '.') + '@icehsiocio.gov';
}

function mono(name?: string): string {
  return (name ?? '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className={`mini${copied ? ' done' : ''}`}
      onClick={() => {
        navigator.clipboard?.writeText(text)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
          })
          .catch(() => {});
      }}
    >{copied ? 'Copied' : 'Copy'}</button>
  );
}

function ContactRows({ person }: { person: OrgChartDoc['org']['pm'] }) {
  const em = mail(person);
  const ph = person.ext ? `+1 202-555-${person.ext}` : null;
  const presence = person.presence ?? 'available';
  const pr = presence[0].toUpperCase() + presence.slice(1);
  return (
    <>
      <div className="crow"><div className="ic">✉</div>
        <div className="val"><span className="lbl">Email</span>
          <a href={`mailto:${em}`}>{em}</a></div>
        <CopyButton text={em} /></div>
      <div className="crow"><div className="ic">▣</div>
        <div className="val"><span className="lbl">Teams</span>
          <span className={`pres ${presence}`}>{pr}</span></div>
        <a className="mini" href={`https://teams.microsoft.com/l/chat/0/0?users=${em}`} target="_blank" rel="noopener noreferrer">Chat</a></div>
      <div className="crow"><div className="ic">☎</div>
        <div className="val"><span className="lbl">Phone</span>
          {ph ? <a href={`tel:+1202555${person.ext}`}>{ph}</a> : <span className="pres">Not on file</span>}</div>
        {ph && <CopyButton text={ph} />}</div>
    </>
  );
}

function ChainRows({ org, group, prog, person }: { org: OrgChartDoc['org']; group: OrgChartDoc['groups'][0]; prog: OrgChartDoc['groups'][0]['programs'][0]; person: OrgChartDoc['groups'][0]['programs'][0]['people'][0] }) {
  const lead = prog.people.find(p => p.lead) || null;
  const isLead = person === lead;
  return (
    <div className="chain">
      <div className="cn top">
        <div className="n">{org.pm.name} / {org.dpm.name}</div>
        <div className="r">{org.pm.role} · {org.dpm.role}</div>
      </div>
      <div className="cn">
        <div className="n">{group.short}</div>
        <div className="r">Group · {prog.short}</div>
      </div>
      {!isLead && lead && (
        lead.vacant
          ? <div className="cn vac"><div className="n">Vacant · {lead.role}</div><div className="r">{lead.req}</div></div>
          : <div className="cn"><div className="n">{lead.name}</div><div className="r">{lead.role} · Program lead</div></div>
      )}
      {person.vacant
        ? <div className="cn self vac"><div className="n">Vacant · {person.role}</div><div className="r">{person.req} · open requisition</div></div>
        : <div className="cn self"><div className="n">{person.name} <span className="here">You are here</span></div>
            <div className="r">{person.role}{person.lead ? ' · Program lead' : ''}</div></div>}
    </div>
  );
}

function MemberList({ prog, gi, pi, onSelect }: { prog: OrgChartDoc['groups'][0]['programs'][0]; gi: number; pi: number; onSelect: (s: Selection) => void }) {
  return (
    <div className="members">
      {prog.people.map((p, ki) => (
        <button key={ki} className={`mrow${p.vacant ? ' vac' : ''}`} onClick={() => onSelect({ kind: 'person', gi, pi, ki })}>
          <div className="mi">{p.vacant ? '?' : mono(p.name)}</div>
          <div><div className="mn">{p.vacant ? 'Vacant' : p.name}</div><div className="mr">{p.role}</div></div>
          <div className="ml">{p.lead ? 'Lead' : (p.vacant ? (p.req || '') : '')}</div>
        </button>
      ))}
    </div>
  );
}

function PanelBar({ title, closeButtonRef, onClose }: { title: string; closeButtonRef: React.RefObject<HTMLButtonElement>; onClose: () => void }) {
  return (
    <div className="pn-bar"><span>{title}</span>
      <button className="pn-close" ref={closeButtonRef} onClick={onClose} aria-label="Close">✕</button></div>
  );
}

function SunView({ doc, onClose, closeButtonRef }: { doc: OrgChartDoc; onClose: () => void; closeButtonRef: React.RefObject<HTMLButtonElement> }) {
  const { org } = doc;
  return (
    <>
      <PanelBar title="Program Leadership" closeButtonRef={closeButtonRef} onClose={onClose} />
      <div className="pn-head">
        <div className="pn-id">
          <div className="pn-mono">{mono(org.pm.name)}</div>
          <div><div className="pn-name" id="pn-name">{org.pm.name}</div>
               <div className="pn-role">{org.pm.role}</div></div>
        </div>
        <div className="pn-tags"><span className="tag l">Program Manager</span>
          <span className="tag">{org.code}</span></div>
      </div>
      <div className="sec"><h4>Contact · PM</h4><ContactRows person={org.pm} /></div>
      <div className="sec">
        <div className="pn-id" style={{ marginBottom: 14 }}>
          <div className="pn-mono dep">{mono(org.dpm.name)}</div>
          <div><div className="pn-name">{org.dpm.name}</div>
               <div className="pn-role">{org.dpm.role}</div></div>
        </div>
        <h4>Contact · DPM</h4><ContactRows person={org.dpm} />
      </div>
    </>
  );
}

function GroupView({ doc, gi, onClose, onSelect, closeButtonRef }: { doc: OrgChartDoc; gi: number; onClose: () => void; onSelect: (s: Selection) => void; closeButtonRef: React.RefObject<HTMLButtonElement> }) {
  const gr = doc.groups[gi];
  const progs = gr.programs;
  const totalPpl = progs.reduce((a, p) => a + p.people.length, 0);
  const totalVac = progs.reduce((a, p) => a + p.people.filter(x => x.vacant).length, 0);
  return (
    <>
      <PanelBar title={gr.short} closeButtonRef={closeButtonRef} onClose={onClose} />
      <div className="pn-head">
        <div className="pn-id">
          <div className="pn-mono" style={{ fontSize: 11, color: 'var(--teal)', background: 'rgba(46,125,91,.1)', borderColor: 'var(--teal)' }}>{gr.id}</div>
          <div><div className="pn-name" id="pn-name">{gr.label.replace('\n', ' ')}</div>
               <div className="pn-role">{totalPpl} people · {totalVac} vacant{gr.task ? ` · ${gr.task}` : ''}</div></div>
        </div>
        <div className="pn-tags"><span className="tag g">Group</span>
          {totalVac > 0 && <span className="tag vac">{totalVac} vacant</span>}</div>
      </div>
      <div className="sec"><h4>Programs in this group</h4>
        <p className="sub">Click a program to see its team.</p>
        <div className="members">
          {progs.map((p, pi) => (
            <button key={pi} className="mrow" onClick={() => onSelect({ kind: 'prog', gi, pi })}>
              <div className="mi" style={{ fontSize: 10 }}>{p.id}</div>
              <div><div className="mn">{p.short}</div>
                   <div className="mr">{p.people.length} people · {p.people.filter(x => x.vacant).length} vacant</div></div>
              <div className="ml">Open</div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function ProgView({ doc, gi, pi, onClose, onSelect, closeButtonRef }: { doc: OrgChartDoc; gi: number; pi: number; onClose: () => void; onSelect: (s: Selection) => void; closeButtonRef: React.RefObject<HTMLButtonElement> }) {
  const gr = doc.groups[gi];
  const prog = gr.programs[pi];
  const lead = prog.people.find(p => p.lead) || null;
  const vacs = prog.people.filter(p => p.vacant);
  return (
    <>
      <PanelBar title={prog.short} closeButtonRef={closeButtonRef} onClose={onClose} />
      <div className="pn-head">
        <div className="pn-id">
          <div className="pn-mono" style={{ fontSize: 11, color: 'var(--slate)', background: 'rgba(14,26,52,.1)', borderColor: 'var(--slate)' }}>{prog.id}</div>
          <div><div className="pn-name" id="pn-name">{prog.label.replace('\n', ' ')}</div>
               <div className="pn-role">{gr.short} · {prog.people.length} FTE · {vacs.length} vacant</div></div>
        </div>
        <div className="pn-tags">
          <span className="tag g">{gr.short}</span>
          <span className="tag p">{prog.id}</span>
          {vacs.length > 0 && <span className="tag vac">{vacs.length} vacant</span>}
        </div>
      </div>
      <div className="sec"><h4>Team · {prog.people.length} people</h4>
        <MemberList prog={prog} gi={gi} pi={pi} onSelect={onSelect} /></div>
      {lead && !lead.vacant && (
        <div className="sec"><h4>Program Lead</h4>
          <p className="sub">{lead.name} · {gr.short}</p>
          <ContactRows person={lead} /></div>
      )}
      {vacs.length > 0 && (
        <div className="sec"><h4>Vacancies · {vacs.length}</h4>
          {vacs.map((v, i) => (
            <div key={i} className="vrow">
              <div><div className="vr-role">{v.role}</div>
                   <div className="vr-meta">Open requisition</div></div>
              <div className="vr-id">{v.req}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function PersonView({ doc, gi, pi, ki, onClose, onSelect, closeButtonRef }: { doc: OrgChartDoc; gi: number; pi: number; ki: number; onClose: () => void; onSelect: (s: Selection) => void; closeButtonRef: React.RefObject<HTMLButtonElement> }) {
  const gr = doc.groups[gi];
  const prog = gr.programs[pi];
  const person = prog.people[ki];
  const vac = !!person.vacant;
  return (
    <>
      <PanelBar title={vac ? 'Vacant Seat' : (person.name ?? '')} closeButtonRef={closeButtonRef} onClose={onClose} />
      <div className="pn-head">
        <div className="pn-id">
          <div className={`pn-mono${vac ? ' vac' : ''}`}>{vac ? '?' : mono(person.name)}</div>
          <div><div className={`pn-name${vac ? ' vac' : ''}`} id="pn-name">{vac ? 'Vacant Seat' : person.name}</div>
               <div className="pn-role">{person.role}</div></div>
        </div>
        <div className="pn-tags">
          <button className="tag g" onClick={() => onSelect({ kind: 'prog', gi, pi })}>{prog.short} →</button>
          <span className="tag p">{prog.id}</span>
          {person.lead && <span className="tag l">Program lead</span>}
          {vac && <span className="tag vac">{person.req}</span>}
          {person.task && <span className="tag">{person.task}</span>}
        </div>
      </div>
      {vac ? (
        <div className="sec"><h4>Requisition</h4>
          <div className="crow"><div className="ic">#</div>
            <div className="val"><span className="lbl">Req ID</span>{person.req}</div></div>
          <div className="crow"><div className="ic">◇</div>
            <div className="val"><span className="lbl">Managed by</span>{gr.short} · {prog.short}</div></div>
        </div>
      ) : (
        <div className="sec"><h4>Contact</h4>
          <p className="sub">ICE HSI OCIO directory</p><ContactRows person={person} /></div>
      )}
      <div className="sec"><h4>Reporting line</h4>
        <p className="sub">{gr.short} · {prog.short}</p>
        <ChainRows org={doc.org} group={gr} prog={prog} person={person} /></div>
    </>
  );
}

function renderView(selection: Exclude<Selection, null>, doc: OrgChartDoc, onSelect: (s: Selection) => void, onClose: () => void, closeButtonRef: React.RefObject<HTMLButtonElement>) {
  switch (selection.kind) {
    case 'sun':
      return <SunView doc={doc} onClose={onClose} closeButtonRef={closeButtonRef} />;
    case 'group':
      return <GroupView doc={doc} gi={selection.gi} onClose={onClose} onSelect={onSelect} closeButtonRef={closeButtonRef} />;
    case 'prog':
      return <ProgView doc={doc} gi={selection.gi} pi={selection.pi} onClose={onClose} onSelect={onSelect} closeButtonRef={closeButtonRef} />;
    case 'person':
      return <PersonView doc={doc} gi={selection.gi} pi={selection.pi} ki={selection.ki} onClose={onClose} onSelect={onSelect} closeButtonRef={closeButtonRef} />;
    default:
      return null;
  }
}

export default function DrillDownPanel({ doc, selection, onClose, onSelect }: DrillDownPanelProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null!);
  const open = selection !== null;

  const panelRef = useOverlay(open, onClose, { lockScroll: true, closeOnOutsideClick: false }) as React.RefObject<HTMLElement>;

  useEffect(() => {
    if (!open) return;
    if (panelRef.current) panelRef.current.scrollTop = 0;
    closeButtonRef.current?.focus();
  }, [open, selection, panelRef]);

  return (
    <>
      <div className={`scrim${open ? ' on is-on' : ''}`} onClick={onClose} />
      <aside className={`panel drawer${open ? ' on is-on' : ''}`} id="panel" role="dialog" aria-modal="true"
        aria-labelledby="pn-name" aria-hidden={!open} ref={panelRef}>
        {open && renderView(
          selection as Exclude<Selection, null>,
          doc, onSelect, onClose, closeButtonRef,
        )}
      </aside>
    </>
  );
}
